import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Internal_Token,
  getCustomers,
  generateMobileOtp,
  mobileOtpVerification,
  updateCustomers,
  signUpCustomer,
} from "../../services/auth";

import {
  saveToken,
  getSavedToken,
  removeToken,
  isTokenExpired,
} from "../../utils/tokenHelper";

import { encrypt } from "@/utils/encodingdecoding";

const tokenBody = {
  clientid: encrypt("consumerjourneys_130526072112639142536751413916"),
  grant_type: "client_credentials",
  clientsecret: encrypt("KITQMRQORX"),
};

export const loginFlow = createAsyncThunk(
  "auth/loginFlow",
  async (mobile, { rejectWithValue }) => {
    try {
      const tokenRes = await Internal_Token(tokenBody);

      if (!tokenRes?.access_token) {
        return rejectWithValue("Token not generated");
      }
      saveToken(tokenRes);
      const token = tokenRes.access_token;
      let res = await getCustomers(token, { mobile });
      let isNewUser = false;

      if (
        res?.message === "Customer not found" ||
        res?.message === "Invalid Credentials"
      ) {
        // Naya user — signup karo
        const signupRes = await signUpCustomer(token, { mobile });

        if (signupRes?.message !== "Request processed successfully.") {
          return rejectWithValue(signupRes?.message || "Signup failed");
        }

        res = await getCustomers(token, { mobile });
        isNewUser = true;
      } else if (res?.message !== "Request processed successfully.") {
        return rejectWithValue(res?.message || "Login failed");
      }
      const profileIncomplete =
        !res?.data?.name || res?.data?.name.trim() === "";
      isNewUser = isNewUser || profileIncomplete;

      localStorage.setItem("mobile", `+91${mobile}`);
      localStorage.setItem("customercode", res?.data?.customercode || "");

      const otpRes = await generateMobileOtp({
        mobile: `+91${mobile}`,
        authkey: "332811AofgzqKp5eeadc8dP1",
        otp_expiry: 15,
        template_id: "65115af8d6fc05216e79d6b2",
      });

      if (otpRes?.type !== "success") {
        return rejectWithValue("Failed to send OTP");
      }

      return { token, phoneNumber: mobile, isNewUser };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const signupFlow = createAsyncThunk(
  "auth/signupFlow",
  async ({ name, mobile, state, district, village, pin, landowned }, { rejectWithValue }) => {
    try {
      const tokenRes = await Internal_Token(tokenBody);

      if (!tokenRes?.access_token) {
        return rejectWithValue("Token not generated");
      }

      saveToken(tokenRes);
      const token = tokenRes.access_token;

      const res = await signUpCustomer(token, { mobile });

      if (res?.message !== "Request processed successfully.") {
        return rejectWithValue(res?.message || "Failed to create customer");
      }

      localStorage.setItem("customercode", res?.data?.customercode);
      localStorage.setItem("mobile", res?.data?.mobile);
      localStorage.setItem("name", res?.data?.name);

      return { token, phoneNumber: mobile };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ otp }, { rejectWithValue, dispatch }) => {
    try {
      const mobile = localStorage.getItem("mobile"); // already +91XXXXXXXX format
      const customercode = localStorage.getItem("customercode");

      let tokenData = getSavedToken();

      if (!tokenData || isTokenExpired(tokenData)) {
        const newToken = await dispatch(refreshToken()).unwrap();
        tokenData = newToken;
      }

      // Jo OTP gaya tha ussi se verify karo
      const verifyRes = await mobileOtpVerification(otp, mobile, {});

      const resultType = verifyRes?.type || verifyRes?.data?.type;

      if (resultType !== "success") {
        return rejectWithValue("OTP verification failed");
      }

      await updateCustomers(tokenData.access_token, customercode, {
        ismobileverified: true,
      });

      localStorage.setItem("isLoggedIn", "true");

      return { mobile };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const newToken = await Internal_Token(tokenBody);
      saveToken(newToken);
      return newToken;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    token: getSavedToken()?.access_token || null,
    isLoggedIn: (() => {
      const tokenData = getSavedToken();
      return (
        localStorage.getItem("isLoggedIn") === "true" &&
        tokenData &&
        !isTokenExpired(tokenData)
      );
    })(),
    phoneNumber: "",
    isNewUser: false,
    loading: false,
    error: null,
  },

  reducers: {
    logout: (state) => {
      state.token = null;
      state.isLoggedIn = false;
      state.isNewUser = false;
      removeToken();
      localStorage.removeItem("qrCode");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("mobile");
      localStorage.removeItem("isNewUser");
      localStorage.removeItem("name");
      localStorage.removeItem("customercode");
      localStorage.setItem("logout", Date.now());
      localStorage.removeItem("lastVoucherCode");
      window.location.replace("/login");
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loginFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.isNewUser = action.payload.isNewUser;
        localStorage.setItem("isNewUser", action.payload.isNewUser ? "true" : "false");
      })
      .addCase(loginFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(signupFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
      })
      .addCase(signupFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.loading = false;
        state.isLoggedIn = true;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;